import { useEffect } from "react";
import { useWatch } from "react-hook-form";

import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  SALE_PAYMENT_METHOD_LABELS,
  SALE_PAYMENT_STATUS_LABELS,
} from "./sale-labels";

import type { SaleFormValues } from "./sale-schema";
import type { Sale } from "@artisancode/api-types";
import type { UseFormReturn } from "react-hook-form";

interface SalePaymentSectionProps {
  form: UseFormReturn<SaleFormValues>;
  editing: Sale | null;
  sequencePreview: string;
  autoGenerate: boolean;
}

export function SalePaymentSection({
  form,
  editing,
  sequencePreview,
  autoGenerate,
}: SalePaymentSectionProps) {
  const paymentStatus = useWatch({
    control: form.control,
    name: "paymentStatus",
  });
  const isUnpaid = paymentStatus === "unpaid";

  useEffect(() => {
    if (isUnpaid) {
      form.setValue("paymentMethod", "credit_unpaid");
    } else if (form.getValues("paymentMethod") === "credit_unpaid") {
      form.setValue("paymentMethod", "cash");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnpaid]);

  return (
    <div className="flex flex-col gap-3">
      <FormField
        control={form.control}
        name="customerName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nama Pelanggan</FormLabel>
            <FormControl>
              <Input placeholder="Nama pelanggan" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="paymentStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status Pembayaran</FormLabel>
            <Select
              value={field.value}
              onValueChange={field.onChange}
              disabled={editing?.hasReceivablePayments}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.entries(SALE_PAYMENT_STATUS_LABELS)
                  // Only "Lunas" and "Belum Bayar" are pickable here:
                  // - "Batal" is a dedicated Void action (see sale-list.tsx)
                  //   that requires a reason — never set it directly.
                  // - "Sebagian" only ever comes from Piutang's own
                  //   computed status (actual verified payments on record);
                  //   picking it manually with no payments would just lie.
                  .filter(([v]) => v === "paid" || v === "unpaid")
                  .map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {editing?.hasReceivablePayments && (
              <p className="text-xs text-muted-foreground">
                Sudah dilacak di Piutang — ubah status lewat halaman Piutang.
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      {!isUnpaid && (
        <FormField
          control={form.control}
          name="paymentMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Metode Pembayaran</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(SALE_PAYMENT_METHOD_LABELS)
                    // credit_unpaid is the auto-assigned marker for unpaid
                    // sales (see effect above) — not a pickable method here.
                    .filter(([v]) => v !== "credit_unpaid")
                    .map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="transactionCode"
        render={({ field }) => {
          const previewSeq = !editing && autoGenerate ? sequencePreview : "";
          return (
            <FormItem>
              <FormLabel>ID Transaksi</FormLabel>
              <FormControl>
                <Input
                  disabled={!editing && autoGenerate}
                  placeholder={
                    editing
                      ? "Opsional"
                      : previewSeq
                        ? `Otomatis: ${previewSeq}`
                        : "Opsional"
                  }
                  {...field}
                />
              </FormControl>
              {!editing && autoGenerate && (
                <p className="text-xs text-muted-foreground">
                  Nomor transaksi selalu otomatis saat pertama dibuat.
                </p>
              )}
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Catatan</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Catatan (opsional)"
                className="min-h-[72px] resize-none"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <WorkspaceCreateField
        control={form.control}
        name="workspaceId"
        editing={!!editing}
      />
    </div>
  );
}
